import { useMemo } from "react";

import { DateTime } from "luxon";

import useBusiness from "./useBusiness";
import useCart from "./useCart";

const STEP_MINUTES = 15;

export interface DeliveryOrPickupAtControllerPropsItem {
	dt: DateTime;
	value: string;
	wd: string;
	day: number;

	times: {
		item: DeliveryOrPickupAtControllerPropsItem;
		dt: undefined | DateTime;
		date: string;
		value: string;
		text: undefined | string;
	}[];
}

export interface DeliveryOrPickupAtControllerProps {
	visible: boolean;

	items: DeliveryOrPickupAtControllerPropsItem[];

	selection: {
		item: DeliveryOrPickupAtControllerPropsItem;
		dt: undefined | DateTime;
		date: string;
		value: string;
		text: undefined | string;
	};

	setValue(iso: string): void;
}

const useDeliveryOrPickupAtController = ({ visible }: { visible }): DeliveryOrPickupAtControllerProps => {
	const { business } = useBusiness();
	const { deliveryOrPickupAt, getDeliveryOrPickupIntervals, setDeliveryOrPickupAt } = useCart();

	const deliveryOrPickupIntervals = getDeliveryOrPickupIntervals();

	const items = useMemo<DeliveryOrPickupAtControllerPropsItem[]>(
		() =>
			deliveryOrPickupIntervals.map(({ date, intervals }, index) => {
				const startOfDay = DateTime.fromISO(date, { zone: business.timeZone });
				const nextDay = startOfDay.plus({ days: 1 });

				const times: DeliveryOrPickupAtControllerPropsItem["times"] = [];

				const item = {
					dt: startOfDay,
					value: date,
					wd: startOfDay.weekdayShort,
					day: startOfDay.day,
					times
				};

				if (index === 0) {
					times.push({
						item,
						date,
						dt: undefined,
						value: "",
						text: undefined
					});
				}

				const iter = intervals[Symbol.iterator]();
				let res = iter.next();

				if (!res.done) {
					for (let time = startOfDay; +time < +nextDay; time = time.plus({ minutes: STEP_MINUTES })) {
						while (+time > +res.value.endsAt) {
							res = iter.next();

							if (res.done) {
								break;
							}
						}

						if (res.done) {
							break;
						}

						if (+time >= +res.value.startsAt) {
							times.push({
								item,
								date,
								dt: time,
								value: time.toISO(),
								text: time.toFormat("h:mm a")
							});
						}
					}
				}

				return item;
			}),
		[business.timeZone, deliveryOrPickupIntervals]
	);

	const selection = useMemo<DeliveryOrPickupAtControllerProps["selection"]>(() => {
		if (deliveryOrPickupAt) {
			const zoned = deliveryOrPickupAt.setZone(business.timeZone);
			const item = items.find(x => x.value === zoned.toISODate());

			if (item) {
				const time = item.times.find(x => x.value === zoned.toISO());

				if (time) {
					return time;
				}
			}
		}

		return items[0].times[0];
	}, [business.timeZone, deliveryOrPickupAt, items]);

	const setValue = useMemo(
		() => (iso: string) => {
			if (iso) {
				for (const item of items) {
					for (const time of item.times) {
						if (time.value === iso) {
							setDeliveryOrPickupAt(time.dt);
							return;
						}
					}
				}
			}

			setDeliveryOrPickupAt(undefined);
		},
		[items, setDeliveryOrPickupAt]
	);

	return useMemo<DeliveryOrPickupAtControllerProps>(
		() => ({
			visible,
			items,
			selection,
			setValue
		}),
		[visible, items, selection, setValue]
	);
};

export default useDeliveryOrPickupAtController;
